import axios from 'axios'
import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import * as XLSX from 'xlsx'
import { config } from '../../function/config'
import SuperAdminHeader from './SuperAdminHeader'

export default function AddAdmins() {
  const navigate = useNavigate()
  const token = localStorage.getItem('token')

  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [existingAdmins, setExistingAdmins] = useState([])
  const [existingBranches, setExistingBranches] = useState([])
  const [existingStaffs, setExistingStaffs] = useState([])

  const [selectedFile, setSelectedFile] = useState(null)
  const [openUploadModal, setOpenUploadModal] = useState(false)

  const [formData, setFormData] = useState({
    admin_name: '',
    address: '',
    phone: '',
    email: ''
  })

  useEffect(() => {
    const fetchExistingData = async () => {
      try {
        const headers = {
          Authorization: `Token ${token}`
        }

        const [
          adminsResponse,
          branchesResponse,
          staffsResponse
        ] = await Promise.all([
          axios.get(
            `${config.baseurl}get_all_admins/`,
            { headers }
          ),
          axios.get(
            `${config.baseurl}get_all_branches/`,
            { headers }
          ),
          axios.get(
            `${config.baseurl}get_all_staffs/`,
            { headers }
          )
        ])

        // Handle both response formats:
        // 1. { data: [...] }
        // 2. [...]

        const admins = Array.isArray(adminsResponse.data)
          ? adminsResponse.data
          : adminsResponse.data?.data || []

        const branches = Array.isArray(branchesResponse.data)
          ? branchesResponse.data
          : branchesResponse.data?.data || []

        const staffs = Array.isArray(staffsResponse.data)
          ? staffsResponse.data
          : staffsResponse.data?.data || []

        console.log('Admins:', admins)
        console.log('Branches:', branches)
        console.log('Staffs:', staffs)

        setExistingAdmins(admins)
        setExistingBranches(branches)
        setExistingStaffs(staffs)

      } catch (error) {
        console.error(
          'Failed to fetch existing admins/branches/staff:',
          error.response?.data || error
        )
      }
    }

    if (token) {
      fetchExistingData()
    }
  }, [token])

  const handleChange = (e) => {
    const { name, value } = e.target

    let newValue = value

    // ============================================================
    // FORMAT PHONE
    // ============================================================

    if (name === 'phone') {
      newValue = value
        .replace(/\D/g, '')
        .slice(0, 10)
    }

    // ============================================================
    // FORMAT EMAIL
    // ============================================================

    if (name === 'email') {
      newValue = value.replace(/\s/g, '')
    }

    // ============================================================
    // UPDATE FORM DATA
    // ============================================================

    setFormData((prev) => ({
      ...prev,
      [name]: newValue
    }))

    let error = ''

    // ============================================================
    // ADMIN NAME
    // Check Admin + Branch + Staff
    // ============================================================

    if (name === 'admin_name') {

      if (!newValue.trim()) {
        error = 'Admin Name is required'
      } else {

        const enteredName = newValue
          .trim()
          .toLowerCase()

        // Check Admin
        const adminExists = existingAdmins.some(
          (admin) =>
            String(admin.admin_name || '')
              .trim()
              .toLowerCase() === enteredName
        )

        // Check Branch
        const branchExists = existingBranches.some(
          (branch) =>
            String(branch.branch_name || '')
              .trim()
              .toLowerCase() === enteredName
        )

        // Check Staff
        const staffExists = existingStaffs.some(
          (staff) => {

            const firstName = String(
              staff.first_name || ''
            ).trim()

            const lastName = String(
              staff.last_name || ''
            ).trim()

            const staffName = `${firstName} ${lastName}`
              .trim()
              .toLowerCase()

            return staffName === enteredName
          }
        )

        if (adminExists) {
          error = 'This name already exists as an Admin'
        } else if (branchExists) {
          error = 'This name already exists as a Branch'
        } else if (staffExists) {
          error = 'This name already exists as a Staff'
        }
      }
    }

    // ============================================================
    // ADDRESS
    // ============================================================

    if (name === 'address') {

      if (!newValue.trim()) {
        error = 'Address is required'
      }
    }

    // ============================================================
    // PHONE
    // Check Admin + Branch + Staff
    // ============================================================

    if (name === 'phone') {

      if (!newValue) {
        error = 'Phone number is required'
      }

      else if (!/^\d+$/.test(newValue)) {
        error = 'Phone number must contain only digits'
      }

      else if (!/^[6-9]/.test(newValue)) {
        error = 'Phone number must start with 6, 7, 8 or 9'
      }

      else if (newValue.length < 10) {
        error = 'Phone number must contain exactly 10 digits'
      }

      else {

        const phone = newValue.trim()

        // Admin
        const adminPhoneExists = existingAdmins.some(
          (admin) =>
            String(admin.phone || '').trim() === phone
        )

        // Branch
        const branchPhoneExists = existingBranches.some(
          (branch) =>
            String(branch.phone || '').trim() === phone
        )

        // Staff
        const staffPhoneExists = existingStaffs.some(
          (staff) =>
            String(staff.phone || '').trim() === phone
        )

        if (adminPhoneExists) {
          error =
            'This phone number already exists for an Admin'
        }

        else if (branchPhoneExists) {
          error =
            'This phone number already exists in a Branch'
        }

        else if (staffPhoneExists) {
          error =
            'This phone number already exists for a Staff'
        }
      }
    }

    // ============================================================
    // EMAIL
    // Check Admin + Branch + Staff
    // ============================================================

    if (name === 'email') {

      if (!newValue.trim()) {
        error = 'Email is required'
      }

      else {

        const email = newValue
          .trim()
          .toLowerCase()

        // --------------------------------------------------------
        // Basic Email Validation
        // --------------------------------------------------------

        if (!email.includes('@')) {
          error = 'Missing @ symbol'
        }

        else {

          const parts = email.split('@')

          // More than one @
          if (parts.length !== 2) {
            error = 'Email must contain only one @ symbol'
          }

          else {

            const username = parts[0]
            const domain = parts[1]

            if (!username) {
              error =
                'Username is missing before "@"'
            }

            else if (!domain) {
              error =
                'Domain name is missing'
            }

            else if (!domain.includes('.')) {
              error =
                'Missing "dot(.)" after domain name'
            }

            else {

              const allowedDomains = [
                'gmail.com',
                'yahoo.com',
                'hotmail.com',
                'outlook.com'
              ]

              if (
                !allowedDomains.includes(
                  domain.toLowerCase()
                )
              ) {
                error =
                  'Only gmail.com, yahoo.com, hotmail.com and outlook.com are allowed'
              }

              else {

                // ------------------------------------------------
                // Duplicate Email
                // ------------------------------------------------

                const adminEmailExists =
                  existingAdmins.some(
                    (admin) =>
                      String(admin.email || '')
                        .trim()
                        .toLowerCase() === email
                  )

                const branchEmailExists =
                  existingBranches.some(
                    (branch) =>
                      String(branch.email || '')
                        .trim()
                        .toLowerCase() === email
                  )

                const staffEmailExists =
                  existingStaffs.some(
                    (staff) =>
                      String(staff.email || '')
                        .trim()
                        .toLowerCase() === email
                  )

                if (adminEmailExists) {
                  error =
                    'This email already exists for an Admin'
                }

                else if (branchEmailExists) {
                  error =
                    'This email already exists in a Branch'
                }

                else if (staffEmailExists) {
                  error =
                    'This email already exists for a Staff'
                }
              }
            }
          }
        }
      }
    }

    // ============================================================
    // UPDATE ERROR
    // ============================================================

    setErrors((prev) => ({
      ...prev,
      [name]: error
    }))
  }

  // Validation
  const validateForm = (data) => {
    const newErrors = {}

    // ============================================================
    // ADMIN NAME
    // ============================================================

    if (!data.admin_name.trim()) {
      newErrors.admin_name = 'Admin Name is required'
    } else {

      const adminName = data.admin_name
        .trim()
        .toLowerCase()

      const adminExists = existingAdmins.some(
        (admin) =>
          String(admin.admin_name || '')
            .trim()
            .toLowerCase() === adminName
      )

      const branchExists = existingBranches.some(
        (branch) =>
          String(branch.branch_name || '')
            .trim()
            .toLowerCase() === adminName
      )

      const staffExists = existingStaffs.some(
        (staff) => {

          const staffName = `${String(
            staff.first_name || ''
          ).trim()} ${String(
            staff.last_name || ''
          ).trim()}`
            .trim()
            .toLowerCase()

          return staffName === adminName
        }
      )

      if (adminExists) {
        newErrors.admin_name =
          'This name already exists as an Admin'
      } else if (branchExists) {
        newErrors.admin_name =
          'This name already exists as a Branch'
      } else if (staffExists) {
        newErrors.admin_name =
          'This name already exists as a Staff'
      }
    }

    // ============================================================
    // ADDRESS
    // ============================================================

    if (!data.address.trim()) {
      newErrors.address = 'Address is required'
    }

    // ============================================================
    // EMAIL
    // ============================================================

    if (!data.email.trim()) {
      newErrors.email = 'Email is required'
    } else {

      const email = data.email
        .trim()
        .toLowerCase()

      if (!email.includes('@')) {
        newErrors.email = 'Missing @ Symbol'
      } else {

        const parts = email.split('@')

        if (parts.length !== 2) {
          newErrors.email =
            'Email must contain only one @ symbol'
        } else {

          const username = parts[0]
          const domain = parts[1]

          if (!username) {
            newErrors.email =
              'Username is missing before "@"'
          } else if (!domain) {
            newErrors.email =
              'Domain name is missing'
          } else if (!domain.includes('.')) {
            newErrors.email =
              'Missing "dot(.)" after domain name'
          } else {

            const allowedDomains = [
              'gmail.com',
              'yahoo.com',
              'hotmail.com',
              'outlook.com'
            ]

            if (!allowedDomains.includes(domain)) {
              newErrors.email =
                'Only gmail.com, yahoo.com, hotmail.com and outlook.com are allowed'
            } else {

              const adminEmailExists =
                existingAdmins.some(
                  (admin) =>
                    String(admin.email || '')
                      .trim()
                      .toLowerCase() === email
                )

              const branchEmailExists =
                existingBranches.some(
                  (branch) =>
                    String(branch.email || '')
                      .trim()
                      .toLowerCase() === email
                )

              const staffEmailExists =
                existingStaffs.some(
                  (staff) =>
                    String(staff.email || '')
                      .trim()
                      .toLowerCase() === email
                )

              if (adminEmailExists) {
                newErrors.email =
                  'This email already exists for an Admin'
              } else if (branchEmailExists) {
                newErrors.email =
                  'This email already exists in a Branch'
              } else if (staffEmailExists) {
                newErrors.email =
                  'This email already exists for a Staff'
              }
            }
          }
        }
      }
    }

    // ============================================================
    // PHONE
    // ============================================================

    if (!data.phone) {
      newErrors.phone = 'Phone number is required'
    } else if (!/^\d+$/.test(data.phone)) {
      newErrors.phone =
        'Phone number must contain only digits'
    } else if (!/^[6-9]\d{9}$/.test(data.phone)) {
      newErrors.phone =
        'Phone number must start with 6, 7, 8 or 9 and contain exactly 10 digits'
    } else {

      const phone = data.phone.trim()

      const adminPhoneExists = existingAdmins.some(
        (admin) =>
          String(admin.phone || '').trim() === phone
      )

      const branchPhoneExists = existingBranches.some(
        (branch) =>
          String(branch.phone || '').trim() === phone
      )

      const staffPhoneExists = existingStaffs.some(
        (staff) =>
          String(staff.phone || '').trim() === phone
      )

      if (adminPhoneExists) {
        newErrors.phone =
          'This phone number already exists for an Admin'
      } else if (branchPhoneExists) {
        newErrors.phone =
          'This phone number already exists in a Branch'
      } else if (staffPhoneExists) {
        newErrors.phone =
          'This phone number already exists for a Staff'
      }
    }

    return newErrors
  }

  // Download Excel Template
  const handleExcel = () => {
    const excelData = [
      {
        admin_name: '',
        address: '',
        phone: '',
        email: ''
      }
    ]

    const worksheet = XLSX.utils.json_to_sheet(excelData)

    worksheet['!cols'] = [
      { wch: 25 },
      { wch: 45 },
      { wch: 18 },
      { wch: 30 }
    ]

    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet( workbook, worksheet, 'Admin')
    XLSX.writeFile( workbook, 'Add Admin.xlsx')
    toast.success('Excel file downloaded successfully')
  }

  // Handle Excel File Selection
  const handleFileChange = (e) => {
    const file = e.target.files?.[0]

    if (!file) {
      return
    }

    const allowedExtensions = ['.xlsx', '.xls']
    const fileName = file.name.toLowerCase()
    const isValidFile = allowedExtensions.some((extension) => fileName.endsWith(extension))

    if (!isValidFile) {
      toast.error('Please select an Excel file (.xlsx or .xls)')
      e.target.value = ''
      setSelectedFile(null)
      return
    }

    setSelectedFile(file)
  }

  const getExactErrorMessage = (errorData) => {

    if (!errorData) {
      return 'Unknown error occurred'
    }

    // Example:
    // {message: "Admin already exists"}
    if (typeof errorData === 'string') {
      return errorData
    }

    if (errorData.message) {

      if (typeof errorData.message === 'string') {
        return errorData.message
      }

      if (typeof errorData.message === 'object') {
        return Object.entries(errorData.message)
          .map(([field, messages]) => {

            if (Array.isArray(messages)) {
              return `${field}: ${messages.join(', ')}`
            }

            return `${field}: ${messages}`
          })
          .join(' | ')
      }
    }

    if (typeof errorData === 'object') {

      return Object.entries(errorData)
        .map(([field, messages]) => {

          if (Array.isArray(messages)) {
            return `${field}: ${messages.join(', ')}`
          }

          if (typeof messages === 'object') {
            return `${field}: ${JSON.stringify(messages)}`
          }

          return `${field}: ${messages}`
        })
        .join(' | ')
    }

    return 'Unknown error occurred'
  }

  // Upload Excel File
  const handleUpload = async (e) => {
    e.preventDefault()

    if (!selectedFile) {
      toast.error('Please select an Excel file')
      return
    }

    try {
      setUploading(true)

      // ============================================================
      // READ EXCEL FILE
      // ============================================================

      const arrayBuffer = await selectedFile.arrayBuffer()

      const workbook = XLSX.read(arrayBuffer, {
        type: 'array'
      })

      const sheetName = workbook.SheetNames[0]

      if (!sheetName) {
        toast.error('No worksheet found in Excel file')
        return
      }

      const worksheet = workbook.Sheets[sheetName]

      const excelData = XLSX.utils.sheet_to_json(
        worksheet,
        {
          defval: ''
        }
      )

      if (!excelData.length) {
        toast.error('Excel file is empty')
        return
      }

      // ============================================================
      // FETCH EXISTING ADMIN + BRANCH + STAFF
      // ============================================================

      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Token ${token}`
      }

      const [adminsResponse, branchesResponse, staffsResponse] = await Promise.all([
        axios.get(`${config.baseurl}get_all_admins/`,{ headers }),

        axios.get(`${config.baseurl}get_all_branches/`, { headers }),

        axios.get(`${config.baseurl}get_all_staffs/`, { headers })
      ])

      const existingAdmins = adminsResponse.data?.data || []

      const existingBranches = branchesResponse.data?.data || []

      const existingStaffs = staffsResponse.data?.data || []

      const existingAdminNames = new Set(existingAdmins.filter((admin) => admin.admin_name).map((admin) => String(admin.admin_name).trim().toLowerCase()))
      const existingBranchNames = new Set(existingBranches.filter((branch) => branch.branch_name).map((branch) => String(branch.branch_name).trim().toLowerCase()))
      const existingStaffNames = new Set(existingStaffs.map((staff) => {
        const firstName = String(staff.first_name || '').trim()
        const lastName = String(staff.last_name || '').trim()

        return `${firstName} ${lastName}`.trim().toLowerCase()}).filter((name) => name)
      )

      const existingAdminEmails = new Set(existingAdmins.filter((admin) => admin.email).map((admin) =>String(admin.email).trim().toLowerCase()))
      const existingBranchEmails = new Set(existingBranches.filter((branch) => branch.email).map((branch) => String(branch.email).trim().toLowerCase()))
      const existingStaffEmails = new Set(existingStaffs.filter((staff) => staff.email).map((staff) =>String(staff.email).trim().toLowerCase()))
      const existingAdminPhones = new Set(existingAdmins.filter((admin) => admin.phone).map((admin) => String(admin.phone).trim()))
      const existingBranchPhones = new Set(existingBranches.filter((branch) => branch.phone).map((branch) =>String(branch.phone).trim()))
      const existingStaffPhones = new Set(existingStaffs.filter((staff) => staff.phone).map((staff) => String(staff.phone).trim()))

      const newAdmins = []

      let skippedCount = 0
      let invalidCount = 0
      let successCount = 0
      let failedCount = 0

      const uploadErrors = []

      // ============================================================
      // PROCESS EXCEL ROWS
      // ============================================================

      for (let index = 0; index < excelData.length; index++) {

        const row = excelData[index]

        const excelRowNumber = index + 2

        // ==========================================================
        // ADMIN DATA
        // ==========================================================

        const adminData = {
          admin_name: String(row.admin_name || '' ).trim(),
          address: String(row.address || '' ).trim(),
          phone: String(row.phone || '' ).replace(/\D/g, '').slice(0, 10),
          email: String(row.email || '').trim().toLowerCase()
        }

        // ==========================================================
        // SKIP EMPTY ROW
        // ==========================================================

        if (
          !adminData.admin_name &&
          !adminData.address &&
          !adminData.phone &&
          !adminData.email
        ) {
          continue
        }

        // ==========================================================
        // VALIDATE FORM
        // ==========================================================

        const errors = validateForm(adminData)

        if (
          Object.keys(errors).length > 0
        ) {

          invalidCount++

          const exactValidationError =
            Object.entries(errors)
              .map(([field, message]) => {

                if (Array.isArray(message)) {
                  return `${field}: ${message.join(', ')}`
                }

                return `${field}: ${message}`
              })
              .join(' | ')

          uploadErrors.push(
            `Row ${excelRowNumber}: ${exactValidationError}`
          )

          continue
        }

        // ==========================================================
        // NORMALIZED VALUES
        // ==========================================================

        const adminName =
          adminData.admin_name
            .trim()
            .toLowerCase()

        const adminEmail =
          adminData.email
            .trim()
            .toLowerCase()

        const adminPhone =
          adminData.phone
            .trim()

        // ==========================================================
        // CHECK ADMIN NAME
        // ==========================================================

        const adminNameExists =
          existingAdminNames.has(
            adminName
          )

        // ==========================================================
        // CHECK BRANCH NAME
        // ==========================================================

        const branchNameExists =
          existingBranchNames.has(
            adminName
          )

        // ==========================================================
        // CHECK STAFF NAME
        // ==========================================================

        const staffNameExists =
          existingStaffNames.has(
            adminName
          )

        // ==========================================================
        // CHECK ADMIN EMAIL
        // ==========================================================

        const adminEmailExists =
          adminEmail &&
          existingAdminEmails.has(
            adminEmail
          )

        // ==========================================================
        // CHECK BRANCH EMAIL
        // ==========================================================

        const branchEmailExists =
          adminEmail &&
          existingBranchEmails.has(
            adminEmail
          )

        // ==========================================================
        // CHECK STAFF EMAIL
        // ==========================================================

        const staffEmailExists =
          adminEmail &&
          existingStaffEmails.has(
            adminEmail
          )

        // ==========================================================
        // CHECK ADMIN PHONE
        // ==========================================================

        const adminPhoneExists =
          adminPhone &&
          existingAdminPhones.has(
            adminPhone
          )

        // ==========================================================
        // CHECK BRANCH PHONE
        // ==========================================================

        const branchPhoneExists =
          adminPhone &&
          existingBranchPhones.has(
            adminPhone
          )

        // ==========================================================
        // CHECK STAFF PHONE
        // ==========================================================

        const staffPhoneExists =
          adminPhone &&
          existingStaffPhones.has(
            adminPhone
          )

        // ==========================================================
        // DATABASE DUPLICATE CHECK
        // ==========================================================

        if (
          adminNameExists ||
          branchNameExists ||
          staffNameExists ||
          adminEmailExists ||
          branchEmailExists ||
          staffEmailExists ||
          adminPhoneExists ||
          branchPhoneExists ||
          staffPhoneExists
        ) {

          skippedCount++

          const duplicateReason = []

          // --------------------------------------------------------
          // NAME
          // --------------------------------------------------------

          if (adminNameExists) {
            duplicateReason.push(
              'Admin name already exists'
            )
          }
  // jaijaithu@gmail.com	8571926430
          if (branchNameExists) {
            duplicateReason.push(
              'Name already exists as Branch'
            )
          }

          if (staffNameExists) {
            duplicateReason.push(
              'Name already exists as Staff'
            )
          }

          // --------------------------------------------------------
          // EMAIL
          // --------------------------------------------------------

          if (adminEmailExists) {
            duplicateReason.push(
              'Email already exists for Admin'
            )
          }

          if (branchEmailExists) {
            duplicateReason.push(
              'Email already exists in Branch'
            )
          }

          if (staffEmailExists) {
            duplicateReason.push(
              'Email already exists for Staff'
            )
          }

          // --------------------------------------------------------
          // PHONE
          // --------------------------------------------------------

          if (adminPhoneExists) {
            duplicateReason.push(
              'Phone already exists for Admin'
            )
          }

          if (branchPhoneExists) {
            duplicateReason.push(
              'Phone already exists in Branch'
            )
          }

          if (staffPhoneExists) {
            duplicateReason.push(
              'Phone already exists for Staff'
            )
          }

          uploadErrors.push(
            `Row ${excelRowNumber}: ${duplicateReason.join(' | ')}`
          )

          continue
        }

        // ==========================================================
        // CHECK DUPLICATES INSIDE CURRENT EXCEL
        // ==========================================================

        const duplicateInCurrentUpload =
          newAdmins.some((admin) => {

            const sameName =
              admin.admin_name
                .trim()
                .toLowerCase() ===
              adminData.admin_name
                .trim()
                .toLowerCase()

            const sameEmail =
              adminData.email &&
              admin.email &&
              admin.email
                .trim()
                .toLowerCase() ===
              adminData.email
                .trim()
                .toLowerCase()

            const samePhone =
              adminData.phone &&
              admin.phone &&
              admin.phone.trim() ===
              adminData.phone.trim()

            return (
              sameName ||
              sameEmail ||
              samePhone
            )
          })

        // ==========================================================
        // HANDLE EXCEL DUPLICATE
        // ==========================================================

        if (duplicateInCurrentUpload) {

          skippedCount++

          uploadErrors.push(
            `Row ${excelRowNumber}: Duplicate admin found inside Excel file`
          )

          continue
        }

        // ==========================================================
        // ADD ADMIN
        // ==========================================================

        newAdmins.push({
          ...adminData,
          excelRowNumber
        })

        // ==========================================================
        // UPDATE SETS
        // ==========================================================

        existingAdminNames.add(
          adminName
        )

        if (adminData.email) {
          existingAdminEmails.add(
            adminEmail
          )
        }

        if (adminData.phone) {
          existingAdminPhones.add(
            adminPhone
          )
        }
      }

      // ============================================================
      // NO NEW ADMINS
      // ============================================================

      if (!newAdmins.length) {

        if (uploadErrors.length > 0) {

          uploadErrors.forEach(
            (errorMessage, index) => {

              setTimeout(() => {

                toast.error(
                  errorMessage,
                  {
                    autoClose: 10000
                  }
                )

              }, index * 300)
            }
          )

        } else {

          toast.info(
            `No new Admins found. ${skippedCount} existing/duplicate admins skipped.`
          )
        }

        return
      }

      // ============================================================
      // CREATE ADMINS
      // ============================================================

      for (const admin of newAdmins) {

        try {

          const {
            excelRowNumber,
            ...adminPayload
          } = admin

          const response = await axios.post(
            `${config.baseurl}create_admin/`,
            adminPayload,
            {
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Token ${token}`
              }
            }
          )

          if (
            response.status === 200 ||
            response.status === 201
          ) {

            successCount++

          } else {

            failedCount++

            const exactError =
              getExactErrorMessage(
                response.data
              )

            uploadErrors.push(
              `Row ${excelRowNumber}: ${exactError}`
            )
          }

        } catch (error) {

          console.error(
            'Failed to create admin:',
            admin,
            error.response?.data || error
          )

          failedCount++

          const exactError =
            getExactErrorMessage(
              error.response?.data
            )

          uploadErrors.push(
            `Row ${admin.excelRowNumber}: ${exactError}`
          )
        }
      }

      // ============================================================
      // RESULT TOASTS
      // ============================================================

      if (successCount > 0) {
        toast.success(
          `${successCount} new admin(s) added successfully`
        )
      }

      if (skippedCount > 0) {
        toast.info(
          `${skippedCount} existing/duplicate admin(s) skipped`
        )
      }

      if (invalidCount > 0) {
        toast.warning(
          `${invalidCount} invalid row(s) found`
        )
      }

      if (failedCount > 0) {
        toast.error(
          `${failedCount} admin(s) failed to save`
        )
      }

      // ============================================================
      // SHOW EXACT ERRORS
      // ============================================================

      if (uploadErrors.length > 0) {

        console.log(
          'Excel Upload Errors:',
          uploadErrors
        )

        uploadErrors.forEach(
          (errorMessage, index) => {

            setTimeout(() => {

              toast.error(
                errorMessage,
                {
                  autoClose: 10000
                }
              )

            }, index * 300)
          }
        )
      }

      // ============================================================
      // RESET
      // ============================================================

      setSelectedFile(null)
      setOpenUploadModal(false)

    } catch (error) {

      console.error(
        'Excel upload error:',
        error.response?.data || error
      )

      const exactError =
        error.response?.data
          ? getExactErrorMessage(
              error.response.data
            )
          : error.message

      toast.error(
        exactError ||
        'Failed to process Excel file'
      )

    } finally {

      setUploading(false)
    }
  }  
  // Submit Single Admin
  const handleSubmit = async (e) => {
    e.preventDefault()

    const validationErrors = validateForm(formData)

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)

      const firstError = Object.values(validationErrors)[0]
      toast.error(firstError)
      return
    }

    try {
      setLoading(true)

      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Token ${token}`
      }

      // Get existing Admins and Branches
      const [
        existingAdminsResponse,
        existingBranchesResponse
      ] = await Promise.all([
        axios.get(
          `${config.baseurl}get_all_admins/`,
          { headers }
        ),

        axios.get(
          `${config.baseurl}get_all_branches/`,
          { headers }
        )
      ])

      const existingAdmins =
        existingAdminsResponse.data?.data || []

      const existingBranches =
        existingBranchesResponse.data?.data || []

      const adminName = String(formData.admin_name || '')
        .trim()
        .toLowerCase()

      const email = String(formData.email || '')
        .trim()
        .toLowerCase()

      const phone = String(formData.phone || '')
        .trim()

      // Store all duplicate errors
      const duplicateErrors = []

      const adminNameExists = existingAdmins.some((admin) => {
        const existingName = String(
          admin.admin_name || ''
        )
          .trim()
          .toLowerCase()

        return existingName === adminName
      })

      if (adminNameExists) {
        duplicateErrors.push(
          `Admin Name "${formData.admin_name}" already exists in Admin`
        )
      }

      const branchNameExists = existingBranches.some((branch) => {
        const existingName = String(
          branch.branch_name || ''
        )
          .trim()
          .toLowerCase()

        return existingName === adminName
      })

      if (branchNameExists) {
        duplicateErrors.push(
          `Admin Name "${formData.admin_name}" already exists in Branch`
        )
      }

      const adminEmailExists = existingAdmins.some((admin) => {
        const existingEmail = String(
          admin.email || ''
        )
          .trim()
          .toLowerCase()

        return existingEmail === email
      })

      if (adminEmailExists) {
        duplicateErrors.push(
          `Email "${formData.email}" already exists in Admin`
        )
      }

      const branchEmailExists = existingBranches.some((branch) => {
        const existingEmail = String(
          branch.email || ''
        )
          .trim()
          .toLowerCase()

        return existingEmail === email
      })

      if (branchEmailExists) {
        duplicateErrors.push(
          `Email "${formData.email}" already exists in Branch`
        )
      }

      const adminPhoneExists = existingAdmins.some((admin) => {
        const existingPhone = String(
          admin.phone || ''
        ).trim()

        return existingPhone === phone
      })

      if (adminPhoneExists) {
        duplicateErrors.push(
          `Phone "${formData.phone}" already exists in Admin`
        )
      }

      const branchPhoneExists = existingBranches.some((branch) => {
        const existingPhone = String(
          branch.phone || ''
        ).trim()

        return existingPhone === phone
      })

      if (branchPhoneExists) {
        duplicateErrors.push(
          `Phone "${formData.phone}" already exists in Branch`
        )
      }

      if (duplicateErrors.length > 0) {
        duplicateErrors.forEach((message, index) => {
          setTimeout(() => {
            toast.error(message, {
              autoClose: 10000
            })
          }, index * 300)
        })

        return
      }

      const payload = {
        admin_name: formData.admin_name.trim(),
        address: formData.address.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone.trim()
      }

      const res = await axios.post(
        `${config.baseurl}create_admin/`,
        payload,
        {
          headers
        }
      )

      if (res.status === 200 || res.status === 201) {

        toast.success(
          res.data?.message ||
          'Admin created successfully'
        )

        setFormData({
          admin_name: '',
          address: '',
          phone: '',
          email: ''
        })

        setErrors({})

        setTimeout(() => {
          navigate(-1)
        }, 500)
      }

    } catch (error) {

      console.error(
        'Create Admin Error:',
        error.response?.data || error
      )

      const exactError = getExactErrorMessage(
        error.response?.data
      )

      toast.error(
        exactError || 'Failed to create admin',
        {
          autoClose: 10000
        }
      )

    } finally {
      setLoading(false)
    }
  }

  // Close Upload Modal
  const closeUploadModal = () => {
    if (uploading) {
      return
    }

    setSelectedFile(null)
    setOpenUploadModal(false)
  }

  // Reusable Input Class
  const inputClass ='w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 placeholder:text-slate-400'

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 ml-64 5xl:ml-0">

      <SuperAdminHeader />

      {/* HEADER */}
      <div className="max-w-7xl mx-auto px-6 pt-16 pb-6">
        <div className="items-center justify-between text-center">
          <h1 className="mt-1 text-3xl font-bold text-slate-800">
            ADD ADMIN
          </h1>

          <p className="mt-2 text-sm text-slate-500">
            Create a new admin or add multiple admins using an Excel file.
          </p>
        </div>
      </div>

      {/* FORM SECTION */}
      <div className="max-w-7xl mx-auto px-6 pb-12">
        <div className='mx-auto max-w-5xl mb-4 flex justify-end items-center gap-3'>
        
        {/* Excel Actions */}
          <button
            type="button"
            onClick={handleExcel}
            className="inline-flex items-center justify-center rounded-xl border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700"
          >
            Download Excel File
          </button>

          <button
            type="button"
            onClick={() =>setOpenUploadModal(true)}
            className="inline-flex items-center justify-center rounded-xl bg-[#000435] px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-[#000326]"
          >
            Upload Excel File
          </button>
        </div>
        
        <div className="mx-auto max-w-5xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg">

          {/* Form Header */}
          <div className="border-b border-slate-200 bg-slate-50 px-6 py-5 text-center text-lg">
            <h2 className="text-lg font-bold text-slate-800">
              Admin Registration Form
            </h2>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 md:p-8">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">

              {/* Admin Name */}
              <div className="md:col-span-2">
                <label
                  htmlFor="admin_name"
                  className="mb-2 block text-sm font-semibold text-slate-700"
                >
                  Admin Name
                  <span className="ml-1 text-red-500">
                    *
                  </span>
                </label>

                <input
                  id="admin_name"
                  type="text"
                  name="admin_name"
                  placeholder="Enter Admin Name"
                  value={formData.admin_name}
                  onChange={handleChange}
                  className={`${inputClass} ${
                    errors.admin_name
                      ? 'border-red-500 focus:border-red-600'
                      : ''
                  }`}
                />

                {errors.admin_name && (
                  <p className="mt-1.5 text-xs font-medium text-red-500">
                    {errors.admin_name}
                  </p>
                )}
              </div>

              {/* Address */}
              <div className="md:col-span-2">
                <label
                  htmlFor="address"
                  className="mb-2 block text-sm font-semibold text-slate-700"
                >
                  Address
                  <span className="ml-1 text-red-500">
                    *
                  </span>
                </label>

                <textarea
                  id="address"
                  name="address"
                  rows={4}
                  placeholder="Enter admin's address"
                  value={formData.address}
                  onChange={handleChange}
                  className={`${inputClass} resize-none`}
                />
              </div>

              {/* Phone */}
              <div>
                <label
                  htmlFor="phone"
                  className="mb-2 block text-sm font-semibold text-slate-700"
                >
                  Phone Number
                  <span className="ml-1 text-red-500">
                    *
                  </span>
                </label>

                <input
                  id="phone"
                  type="text"
                  name="phone"
                  placeholder="Enter 10-digit phone number"
                  value={formData.phone}
                  onChange={handleChange}
                  maxLength={10}
                  inputMode="numeric"
                  className={`${inputClass} ${errors.phone
                    ? 'border-red-500 focus:border-red-600'
                    : ''
                  }`}
                />

                {errors.phone && (
                  <p className='mt-1.5 text-xs font-medium text-red-500'>{errors.phone}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <label
                  htmlFor="email"
                  className="mb-2 block text-sm font-semibold text-slate-700"
                >
                  Email Address
                  <span className="ml-1 text-red-500">
                    *
                  </span>
                </label>

                <input
                  id="email"
                  type="email"
                  name="email"
                  placeholder="Enter email address"
                  value={formData.email}
                  onChange={handleChange}
                  className={`${inputClass} ${errors.email
                    ? 'border-red-500 focus:border-red-600'
                    : ''
                  }`}
                />

                {errors.email && (
                  <p className='mt-1.5 text-xs font-medium text-red-500'>{errors.email}</p>
                )}
              </div>
            </div>

            {/* Form Actions */}
            <div className="mt-8 flex flex-row gap-3 border-t border-slate-200 pt-6 4xl:flex-col-reverse justify-end">

              <button
                type="button"
                onClick={() => navigate(-1)}
                disabled={loading}
                className="rounded-xl bg-slate-100 px-6 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={loading || Object.keys(errors).some(key => errors[key])}
                className="rounded-xl bg-[#000435] px-7 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-[#000326] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loading ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* EXCEL UPLOAD MODAL */}
      {openUploadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 px-4 backdrop-blur-sm">
          <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl">
            
            {/* Modal Header */}
            <div className="flex items-center justify-between bg-[#000435] px-6 py-5">
              <div>
                <h2 className="text-xl font-bold text-white">Upload Admins</h2>

                <p className="mt-1 text-sm text-slate-300">
                  Import new Admins using an Excel file.
                </p>
              </div>

              <button
                type="button"
                onClick={closeUploadModal}
                disabled={uploading}
                aria-label="Close"
                className="flex h-9 w-9 items-center justify-center rounded-lg text-2xl text-red-500 hover:text-red-600 transition disabled:cursor-not-allowed disabled:opacity-50"
              >
                &times;
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleUpload} className="p-6">

              {/* File Selection */}
              <div className="rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 p-8 text-center transition hover:border-blue-300">
                <div className="mb-5">
                  <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-blue-100">
                    <span className="text-2xl">📊</span>
                  </div>

                  <p className="text-lg font-bold text-slate-800">
                    Select Excel File
                  </p>

                  <p className="mt-1 text-sm text-slate-500">
                    Supported formats: .xlsx and .xls
                  </p>
                </div>

                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileChange}
                  disabled={uploading}
                  className="block w-full text-sm text-slate-600 file:mr-4 file:rounded-lg file:border-0 file:bg-white file:border-r file:border-r-slate-300 file:px-4 file:py-2 file:font-semibold file:text-slate-700 file:shadow-sm hover:file:bg-blue-50 border border-slate-300 rounded-lg bg-slate-300"
                />

                {selectedFile && (
                  <div className="mt-4 rounded-xl border border-blue-200 bg-blue-50 p-4 text-left">
                    <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">
                      Selected File
                    </p>

                    <p className="mt-1 break-all text-sm font-medium text-blue-900">
                      {selectedFile.name}
                    </p>
                  </div>
                )}
              </div>

              {/* Modal Actions */}
              <div className="mt-8 flex flex-col-reverse gap-3 border-t border-slate-200 pt-6 sm:flex-row justify-end">
                <button
                  type="button"
                  onClick={closeUploadModal}
                  disabled={uploading}
                  className="rounded-xl bg-slate-100 px-5 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={ uploading || !selectedFile || Object.keys(errors).some(key => errors[key])}
                  className="rounded-xl bg-[#000435] px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-[#000326] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {uploading ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
